import type { TInvestorEntryErrorCopy, TInvestorEntryErrorReason } from '../types/investor-entry.types';

export const INVESTOR_ENTRY_ERROR_REASONS = [
  'link_missing',
  'link_invalid',
  'portal_not_configured',
  'session_unavailable',
  'checkout_unavailable',
  'signin_unavailable',
  'session_not_established',
  'unexpected',
] as const;

export const DEFAULT_INVESTOR_ENTRY_ERROR_REASON: TInvestorEntryErrorReason = 'unexpected';

// Distinct copy per status: a tampered link and a misconfigured portal are different support
// tickets.
//
// The FE owns this text because the backend's `message` is diagnostic — "Origin mismatch",
// "Missing key" — rather than something an investor can act on. Once those become investor-facing
// we render them instead, keeping this as the fallback: over half these reasons never reach the
// backend at all (no link in the URL, the SDK failing to load, a 200 the client rejects), so
// something local always has to answer.
export const INVESTOR_ENTRY_ERROR_COPY: Record<TInvestorEntryErrorReason, TInvestorEntryErrorCopy> = {
  link_missing: {
    heading: 'This link is incomplete',
    description: 'Open the portal using the full link your fund house sent you. Copying only part of it leaves out the details we need.',
    isRetryable: false,
  },
  link_invalid: {
    heading: 'This link is no longer valid',
    description: 'We could not verify this link. Request a fresh one from your fund house and try again.',
    isRetryable: false,
  },
  portal_not_configured: {
    heading: 'This portal is not set up yet',
    description: 'This address is not a configured investor portal. Please contact your fund house.',
    isRetryable: false,
  },
  session_unavailable: {
    heading: 'We could not start your investment',
    description: 'Something went wrong on our side while setting things up. Please try again in a moment.',
    isRetryable: true,
  },
  checkout_unavailable: {
    heading: 'We could not load the checkout',
    description: 'The investment checkout failed to load. Please check your connection and try again.',
    isRetryable: true,
  },
  // Investment landed but resolve still reports no customer. Not retryable — that would mint
  // another payment session.
  signin_unavailable: {
    heading: 'We could not sign you in',
    description: 'Your investment went through. Open the portal again from your fund house to view it.',
    isRetryable: false,
  },
  // The code verified but the session it returned is already unusable — an expiry we cannot
  // parse, one already in the past, or a device clock far enough ahead to make it look expired.
  // Deliberately not retryable: retrying sends another code and lands in the same place, and
  // each attempt spends a rate-limited send.
  session_not_established: {
    heading: 'We could not keep you signed in',
    description:
      'Your code was correct, but the session did not start. Check your device date and time, then open the portal again from your fund house.',
    isRetryable: false,
  },
  unexpected: {
    heading: 'Something went wrong',
    description: 'An unexpected error occurred. Please try again in a moment.',
    isRetryable: true,
  },
};
